/**
 * Filter controls for the one generic feed widget.
 *
 * The scope row selects a virtual dimension (My Feed is the role-aware default;
 * Booking / Customer / Sales Person / Operator / Lane / Management are
 * drill-downs). Horizon, severity, and event type sit as orthogonal overlays
 * (rebook vs first-book are separate types). All write to the shared
 * FeedContext so every feed surface (right rail, home widget) stays in sync.
 */
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import { formatDimensionValue } from "@/lib/formatDimensionValue";

import { formatFeedPartyLabel } from "./feedViewerParty";
import { cn } from "@/lib/utils";
import { SensitiveText } from "@/components/common/Sensitive";
import { useUser } from "@/auth/UserContext";
import { useFeed } from "@/context/FeedContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { api } from "@/services/api";
import type {
  FeedSeverity,
  ScopeDimension,
  TimeHorizon,
} from "@/types/feed";
import {
  EVENT_TYPE_LABEL,
  feedFilterEventTypesForRole,
  SCOPE_DIMENSION_LABEL,
  SEVERITY_ICON,
  SEVERITY_LABEL,
  SEVERITY_TEXT,
  visibleScopeDimensions,
} from "./feedVisuals";
import { FeedReviewStatusToggle } from "./FeedReviewStatusToggle";
import "./feedFilterBar.css";

const SCOPE_KEY_DATALIST_ID = "feed-scope-key-options";

const HORIZONS: { value: TimeHorizon | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "immediate", label: "Now" },
  { value: "developing", label: "Developing" },
  { value: "strategic", label: "Strategic" },
];

const SEVERITIES: { value: FeedSeverity | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "info", label: SEVERITY_LABEL.info },
  { value: "warning", label: SEVERITY_LABEL.warning },
  { value: "urgent", label: SEVERITY_LABEL.urgent },
];


/** Dimensions that need an entity key to resolve a drill-down. */
const KEYED_DIMENSIONS: ScopeDimension[] = [
  "booking",
  "customer",
  "sales_person",
  "operator",
  "agent",
  "carrier",
  "lane",
];

const KEY_PLACEHOLDER: Partial<Record<ScopeDimension, string>> = {
  booking: "Booking source id (e.g. SRC-123)",
  customer: "Consignee",
  sales_person: "Sales person",
  operator: "Operator (Open By)",
  agent: "Agent (e.g. toptrans)",
  carrier: "Carrier (e.g. msc)",
  lane: "Lane key (e.g. CNNGB-ILASH)",
};

const DIMENSION_HINT: Partial<Record<ScopeDimension, string>> = {
  sales_person: "Aggregate sales-person trends. Pick a rep to drill into their bookings.",
  customer: "Aggregate customer trends. Pick a customer to drill into their bookings.",
  operator: "Insights and alerts by operator. Pick an operator to see their feed.",
  agent: "Insights and alerts by handling agent. Pick an agent to see their feed.",
  carrier: "Insights and alerts by carrier. Pick a carrier to see their feed.",
};

function chipClass(active: boolean): string {
  return cn("feed-filter-chip", active && "feed-filter-chip--active");
}

function keyFilterProperty(dimension: ScopeDimension): string | null {
  if (dimension === "customer") return "consignee";
  if (dimension === "sales_person") return "sales_person";
  if (dimension === "operator") return "opened_by";
  if (dimension === "agent") return "agent";
  if (dimension === "carrier") return "carrier";
  return null;
}

function keyDisplayProperty(dimension: ScopeDimension): string {
  if (dimension === "customer") return "consignee";
  if (dimension === "operator") return "opened_by";
  if (dimension === "agent") return "agent";
  if (dimension === "carrier") return "carrier";
  return "sales_person";
}

function scopeKeyDisplayLabel(
  dimension: ScopeDimension,
  scopeKey: string,
  maskScopeKey: boolean,
): string {
  if (maskScopeKey) {
    const partyDim =
      dimension === "operator"
        ? "opened_by"
        : dimension === "sales_person" || dimension === "agent"
          ? dimension
          : keyDisplayProperty(dimension);
    if (
      partyDim === "sales_person" ||
      partyDim === "opened_by" ||
      partyDim === "agent"
    ) {
      return formatFeedPartyLabel(partyDim, scopeKey);
    }
    return formatDimensionValue(keyDisplayProperty(dimension), scopeKey);
  }
  return scopeKey;
}

export function FeedFilterBar({
  className,
  hideKeyedInput = false,
  hideReviewStatus = false,
  /** Horizontal scroll for scope chips — best in the narrow right rail. */
  compactScope = false,
}: {
  className?: string;
  hideKeyedInput?: boolean;
  hideReviewStatus?: boolean;
  compactScope?: boolean;
}) {
  const { profile } = useUser();
  const { preferences } = useUserPreferences();
  const { filters, setFilters, setScope, resetScope } = useFeed();
  const [keyDraft, setKeyDraft] = useState("");
  const [keyOptions, setKeyOptions] = useState<string[]>([]);

  const role = profile?.role;
  const hasAgentAssignment = (profile?.agent_values?.length ?? 0) > 0;
  const dimensions = useMemo(
    () =>
      visibleScopeDimensions(
        role,
        hasAgentAssignment,
        preferences.scopeToMyBookings,
      ),
    [role, hasAgentAssignment, preferences.scopeToMyBookings],
  );

  const eventTypeOptions = useMemo(() => {
    const types = feedFilterEventTypesForRole(role);
    return [
      { value: "all" as const, label: "All types" },
      ...types.map((type) => ({
        value: type,
        label: EVENT_TYPE_LABEL[type],
      })),
    ];
  }, [role]);

  useEffect(() => {
    const property = keyFilterProperty(filters.scopeDimension);
    if (!property) {
      setKeyOptions([]);
      return;
    }
    let cancelled = false;
    api
      .getBookingFilterValues(property, undefined, 100)
      .then((rows) => {
        if (!cancelled) setKeyOptions(rows.map((r) => r.value));
      })
      .catch(() => {
        if (!cancelled) setKeyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.scopeDimension]);

  const selectDimension = (dimension: ScopeDimension) => {
    if (dimension === "my") {
      resetScope();
      setKeyDraft("");
      return;
    }
    setScope(dimension, undefined);
    setKeyDraft("");
  };

  const applyKey = () => {
    const trimmed = keyDraft.trim();
    if (trimmed) setFilters({ scopeKey: trimmed });
  };

  const needsKey = KEYED_DIMENSIONS.includes(filters.scopeDimension);

  const maskScopeKey =
    filters.scopeDimension === "customer" ||
    filters.scopeDimension === "sales_person" ||
    filters.scopeDimension === "operator" ||
    filters.scopeDimension === "agent";

  const dimensionHint =
    !filters.scopeKey && DIMENSION_HINT[filters.scopeDimension];

  // Org admins (strictly `Admin`, not `SystemAdmin`) get a curated My Feed:
  // custom events assigned to them plus tenant/system signals, not the raw
  // per-booking tenant timeline. Surface a one-line hint pointing them to the
  // drill-downs for full activity.
  const showAdminMyFeedHint =
    role === "Admin" && filters.scopeDimension === "my" && !filters.eventType;

  const scopeTrack = (
    <div
      className={cn(
        "feed-filter-track",
        compactScope && "feed-filter-track--scroll",
      )}
      role="group"
      aria-label="Feed scope"
    >
      {dimensions.map((dimension) => (
        <button
          key={dimension}
          type="button"
          onClick={() => selectDimension(dimension)}
          className={chipClass(filters.scopeDimension === dimension)}
        >
          {SCOPE_DIMENSION_LABEL[dimension]}
        </button>
      ))}
    </div>
  );

  return (
    <div className={cn("feed-filter-bar", className)}>
      {!hideReviewStatus && <FeedReviewStatusToggle />}

      {compactScope ? (
        <div className="feed-filter-scope-scroll">{scopeTrack}</div>
      ) : (
        scopeTrack
      )}

      {dimensionHint && (
        <p className="feed-filter-hint">{dimensionHint}</p>
      )}

      {showAdminMyFeedHint && (
        <p className="feed-filter-hint">
          Showing alerts, insights, and events assigned to you. Open a booking or
          use a scope tab for the full activity timeline.
        </p>
      )}

      {needsKey && !hideKeyedInput && (
        <div className="feed-filter-key-row">
          {filters.scopeKey ? (
            <span className="feed-filter-key-chip min-w-0">
              <span className="truncate">
                {maskScopeKey ? (
                  <SensitiveText
                    value={scopeKeyDisplayLabel(
                      filters.scopeDimension,
                      filters.scopeKey,
                      maskScopeKey,
                    )}
                  />
                ) : (
                  scopeKeyDisplayLabel(
                    filters.scopeDimension,
                    filters.scopeKey,
                    maskScopeKey,
                  )
                )}
              </span>
              <button
                type="button"
                aria-label="Clear entity filter"
                onClick={() => setFilters({ scopeKey: undefined })}
                className="shrink-0 rounded-full p-0.5 hover:bg-primary/15"
              >
                <X className="size-2.5" />
              </button>
            </span>
          ) : (
            <>
              <input
                value={keyDraft}
                onChange={(e) => setKeyDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyKey()}
                placeholder={KEY_PLACEHOLDER[filters.scopeDimension]}
                list={keyOptions.length > 0 ? SCOPE_KEY_DATALIST_ID : undefined}
                className="feed-filter-key-input"
              />
              {keyOptions.length > 0 && (
                <datalist id={SCOPE_KEY_DATALIST_ID}>
                  {keyOptions.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
              )}
              <button
                type="button"
                onClick={applyKey}
                className={cn("feed-filter-chip feed-filter-chip--active shrink-0")}
              >
                Apply
              </button>
            </>
          )}
        </div>
      )}

      <div className="feed-filter-split-row">
        <div
          className="feed-filter-track"
          role="group"
          aria-label="Time horizon"
        >
          {HORIZONS.map(({ value, label }) => {
            const active =
              value === "all"
                ? !filters.timeHorizon
                : filters.timeHorizon === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setFilters({
                    timeHorizon: value === "all" ? undefined : value,
                  })
                }
                className={chipClass(active)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="feed-filter-split" role="separator" aria-hidden />
        <div
          className="feed-filter-track"
          role="group"
          aria-label="Severity"
        >
          {SEVERITIES.map(({ value, label }) => {
            const active =
              value === "all"
                ? !filters.severity
                : filters.severity === value;
            const Icon = value === "all" ? null : SEVERITY_ICON[value];
            return (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setFilters({
                    severity: value === "all" ? undefined : value,
                  })
                }
                className={chipClass(active)}
              >
                {Icon && (
                  <Icon
                    className={cn(
                      "size-2.5 shrink-0",
                      active && value !== "all" && SEVERITY_TEXT[value],
                    )}
                    aria-hidden
                  />
                )}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="feed-filter-track"
        role="group"
        aria-label="Event type"
      >
        {eventTypeOptions.map(({ value, label }) => {
          const active =
            value === "all"
              ? !filters.eventType
              : filters.eventType === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() =>
                setFilters({
                  eventType: value === "all" ? undefined : value,
                })
              }
              className={chipClass(active)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
